// Function index:
//   RefreshCtx          — handles + indexes the refresh step reads.
//   refreshScene(ctx, q, r) — repaint room cards + door lines + scroll
//       into view. Called on every move; cheap because the work scales
//       with rooms, not hexes.
//
// Fog of war:
//   - Current room: highlighted, full info.
//   - Visited room: full info, normal opacity.
//   - Adjacent-to-current (not visited): outline only, dimmed, clickable.
//   - All others: hidden.
//   Doors only render when both endpoints are at least visible.

import type { BootstrapRoom } from '../api/types.js';
import { PAD, CELL_W, CELL_H, ROOM_W, ROOM_H, escapeHtml } from './layout.js';
import type { DoorLineHandle } from './dom.js';
import type { RoomCharacterInput, RoomNpcInput } from './types.js';

export interface RefreshCtx {
  root: HTMLDivElement;
  roomEl: Map<number, HTMLDivElement>;
  roomById: Map<number, BootstrapRoom>;
  roomByKey: Map<string, BootstrapRoom>;
  connectedFrom: Map<number, Set<number>>;
  visitedIds: Set<number>;
  doorLines: DoorLineHandle[];
  characters: Map<number, RoomCharacterInput>;
  npcsByKey: Map<string, RoomNpcInput[]>;
}

export function refreshScene(ctx: RefreshCtx, q: number, r: number): void {
  const { root, roomEl, roomById, roomByKey, connectedFrom,
          visitedIds, doorLines, characters, npcsByKey } = ctx;

  const here = roomByKey.get(`${q},${r}`);
  if (here) visitedIds.add(here.id); // current room is always visited
  const reachableIds = here ? (connectedFrom.get(here.id) ?? new Set<number>()) : new Set<number>();

  const visibleIds = new Set<number>(visitedIds);
  for (const id of reachableIds) visibleIds.add(id);

  for (const [id, el] of roomEl) {
    const room = roomById.get(id)!;
    const isCurrent = room.x === q && room.y === r;
    const isVisited = visitedIds.has(id);
    const isReachable = reachableIds.has(id);
    const isVisible = visibleIds.has(id);
    el.style.display = isVisible ? 'block' : 'none';
    el.classList.toggle('current', isCurrent);
    el.classList.toggle('reachable', isReachable);
    el.classList.toggle('visited', isVisited);
    el.style.borderColor = isCurrent
      ? '#ffeb88'
      : isReachable ? '#8a7a3a' : '#2a2a2a';
    el.style.background = isCurrent ? '#2a200a' : '#141414';
    el.style.cursor = isReachable ? 'pointer' : 'default';
    el.style.opacity = isVisited || isCurrent ? '1' : '0.55';
    // Content: visited rooms show name + kind + characters + NPCs +
    // description-on-hover. Unvisited-but-reachable shows a placeholder
    // name + the kind so the player has a sense of what's beyond the
    // door — no character / NPC spoilers.
    if (isVisited || isCurrent) {
      el.innerHTML = renderVisitedRoom(room, characters, npcsByKey);
      el.title = room.description;
    } else {
      el.innerHTML = renderUnvisitedRoom(room);
      el.title = 'You haven\'t been here yet.';
    }
  }

  // Door lines visible only between two visible rooms; otherwise hidden.
  for (const dl of doorLines) {
    const both = visibleIds.has(dl.fromId) && visibleIds.has(dl.toId);
    dl.line.style.display = both ? '' : 'none';
  }

  // Centre the scroll on the current room so a fresh enter doesn't
  // dump the player on an off-screen corner.
  if (here) {
    const targetX = PAD + here.x * CELL_W + ROOM_W / 2 - root.clientWidth / 2;
    const targetY = PAD + here.y * CELL_H + ROOM_H / 2 - root.clientHeight / 2;
    root.scrollTo({ left: Math.max(0, targetX), top: Math.max(0, targetY), behavior: 'auto' });
  }
}

function renderVisitedRoom(
  room: BootstrapRoom,
  characters: Map<number, RoomCharacterInput>,
  npcsByKey: Map<string, RoomNpcInput[]>,
): string {
  const npcs = npcsByKey.get(`${room.x},${room.y}`) ?? [];
  const charsHere = [...characters.values()].filter((c) => c.q === room.x && c.r === room.y);
  const charsHtml = charsHere.length === 0 ? '' :
    `<div style="margin-top: 4px; display: flex; flex-wrap: wrap; gap: 3px;">${
      charsHere.map((c) => {
        const palette = c.isSelf
          ? 'background: #2a280a; border: 1px solid #ffeb88; color: #ffeb88;'
          : 'background: #0a1a2a; border: 1px solid #1a4a7a; color: #88c0ff;';
        const label = c.name.split(' ')[0] ?? c.name;
        const fullTitle = c.isSelf ? `${c.name} (you)` : c.name;
        return `<span title="${escapeHtml(fullTitle)}" style="display: inline-block; ${palette} font-size: 9px; padding: 1px 5px; border-radius: 999px; line-height: 1.3;">${escapeHtml(label)}</span>`;
      }).join('')
    }</div>`;
  const npcsHtml = npcs.length === 0 ? '' :
    `<div style="margin-top: 3px; display: flex; flex-wrap: wrap; gap: 3px;">${
      npcs.map((n) => `<span title="${escapeHtml(n.archetype)} — ${escapeHtml(n.name)}" style="display: inline-block; background: #2a200a; border: 1px solid #5a4a1a; color: #ffd870; font-size: 9px; padding: 1px 5px; border-radius: 999px; line-height: 1.3;">${escapeHtml(n.name.split(' ')[0] ?? n.name)}</span>`).join('')
    }</div>`;
  return `<div style="font-family: 'Cinzel', serif; font-size: 11px; color: #f0d878; letter-spacing: 0.04em; line-height: 1.3;">${escapeHtml(room.name)}</div><div style="color: #888; font-size: 10px; font-style: italic; margin-top: 2px;">${escapeHtml(room.kind)}${room.is_entrance ? ' · entry' : ''}</div>${charsHtml}${npcsHtml}`;
}

function renderUnvisitedRoom(room: BootstrapRoom): string {
  return `<div style="font-family: 'Cinzel', serif; font-size: 11px; color: #6a6a4a; letter-spacing: 0.04em; line-height: 1.3;">unexplored</div><div style="color: #555; font-size: 10px; font-style: italic; margin-top: 2px;">${escapeHtml(room.kind)}</div>`;
}
